import {useEffect, useState, useRef} from 'react';
import Loadable from 'react-loadable';

import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {WizKnownError} from '@/sdk/common/error';
import {getDocInfoByUrl} from '@/utils/urlUtils';
import {
    reportGenerateAbstractTime,
} from '@/buried/aigc';

import Error from './Error';
import ApplyPermission from './ApplyPermission';
import {getSummaryData} from './summaryData';
import {ISummaryData, CardStatus} from './interface';

import styles from './index.module.scss';

const Normal = Loadable({
    loader() {
        return import('./Normal');
    },
    loading() {
        return <Error status={CardStatus.Loading} />;
    },
});
// TODO: 后续改为importAsyncComponent实现
export const preloadSummaryCard = Normal.preload;

interface IProps {
    url: string;
    updateCardPosition: (height: number) => void;
}

const DocSummaryCard: React.FC<IProps> = ({url, updateCardPosition}) => {
    const [docGuid, setDocGuid] = useState('');
    const [editionId, setEditionId] = useState('');
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<CardStatus>(CardStatus.Loading);
    const [summaryData, setSummaryData] = useState<ISummaryData>();

    const getSummaryDataTimeRef = useRef(0);

    useEffect(() => {
        let docGuid = '';
        const urlObj = new URL(url);
        const docInfo = getDocInfoByUrl(url);
        if (docInfo) {
            docGuid = docInfo.docGuid;
        } else {
            // 短链接的情况
            const pathname = urlObj.pathname;
            const pattern = /\/d\/([\w-]+)/;
            const match = pattern.exec(pathname);
            if (match) {
                docGuid = match[1];
            }
        }

        if (docGuid) {
            const editionId = urlObj.searchParams.get('editionId');
            setEditionId(editionId || '');
            setDocGuid(docGuid);
        }
    }, [url]);

    useEffect(() => {
        // 延时展示卡片
        window.setTimeout(() => {
            setVisible(true);
        }, 300);
    }, []);

    const getData = async (docGuid: string, editionId: string) => {
        getSummaryDataTimeRef.current = Date.now(); // 开始生成摘要，时间点记录

        setStatus(CardStatus.Loading);
        getSummaryData(docGuid, editionId).then(result => {
            setSummaryData(result);
            setStatus(CardStatus.Noraml);

            // 摘要生成耗时, 埋点上报
            if (result?.canSummary && result.summary) {
                reportGenerateAbstractTime({
                    mentionDocId: result?.docGuid || '',
                    delay: Date.now() - getSummaryDataTimeRef.current,
                });
            }
        }).catch(err => {
            if (err instanceof WizKnownError) {
                const errCode = err.toResult().returnCode;
                if (errCode === 16005) {
                    setStatus(CardStatus.NoPermission);
                } else if (errCode === 20125) {
                    setStatus(CardStatus.NotFound);
                } else {
                    setStatus(CardStatus.LoadFail);
                }
            } else {
                setStatus(CardStatus.LoadFail);
            }
        });
    };

    useEffect(() => {
        docGuid && getData(docGuid, editionId);
    }, [docGuid, editionId]);

    // 更新卡片位置
    useEffect(() => {
        if (rootRef.current) {
            updateCardPosition(rootRef.current.offsetHeight);
        }
    }, [updateCardPosition, visible, status]);

    return (
        visible ? <div ref={rootRef}>
            {status === CardStatus.Noraml
                ? (
                    <div className={styles.container}>
                        {summaryData?.docGuid
                            ? <Normal summaryData={summaryData} url={url} editionId={editionId} />
                            : null
                        }
                    </div>
                )
                : status === CardStatus.NoPermission
                    ? <ApplyPermission docGuid={docGuid} />
                    : <Error status={status} />
            }
        </div> : null
    );
};

export default DocSummaryCard;
